import * as THREE from "three";

/**
 * In-game debug overlay: aim the crosshair at anything and it tells you which
 * module drew it, what material it is, and where it sits.
 *
 * The level is merged down to one mesh per material for draw-call reasons, so
 * a raycast hit alone cannot say what it struck.  The Builder keeps a parallel
 * index of every piece it added — owner, material, world bounding box — and
 * this looks the hit point up in that, choosing the tightest box that contains
 * it.  That is what turns "you hit the shingle mesh" into "hero/shed, shingle,
 * centred (-0.9, 2.5, -28.4)".
 */
export class DebugOverlay {
  constructor({ renderer, scene, camera, player, level, cats, els = {} }) {
    this.ctx = { renderer, scene, camera, player, level, cats };
    this.visible = false;
    this.ray = new THREE.Raycaster();
    this.centre = new THREE.Vector2(0, 0);
    this.last = null;

    // The elements arrive as handles (loop.js passes ui/debug.js's pieces):
    // this overlay queries nothing it did not make.
    this.el = els.root;
    // The cat switcher rides with the overlay rather than living on screen the
    // whole time: picking a cat is a developer affordance, and a panel naming
    // five of them is the first thing a player looks at instead of the street.
    // Toggled from here so there is one source of truth for whether the
    // developer view is up -- a second Backquote listener in main.js would
    // drift out of step the moment anything called toggle() directly.  #hud
    // is another module's panel (ui/hud.js), so it travels in as a handle too.
    this.hud = els.hud;
    this.body = els.body;
    this.cross = els.cross;
    this.toast = els.toast;

    addEventListener("keydown", (e) => {
      if (e.code === "F3" || e.code === "Backquote") {
        e.preventDefault();
        this.toggle();
      } else if (e.code === "KeyX" && this.visible) {
        e.preventDefault();
        this.copy();
      }
    });
  }

  toggle() {
    this.visible = !this.visible;
    this.el.classList.toggle("hidden", !this.visible);
    this.cross.classList.toggle("hidden", !this.visible);
    this.hud?.classList.toggle("hidden", !this.visible);
  }

  /** The tightest indexed piece containing (or nearest to) a world point. */
  pieceAt(point) {
    const pieces = this.ctx.level.pieces || [];
    let best = null, bestVol = Infinity;
    for (const p of pieces) {
      const b = p.box;
      if (point.x < b.min.x - 0.05 || point.x > b.max.x + 0.05) continue;
      if (point.y < b.min.y - 0.05 || point.y > b.max.y + 0.05) continue;
      if (point.z < b.min.z - 0.05 || point.z > b.max.z + 0.05) continue;
      const v = (b.max.x - b.min.x) * (b.max.y - b.min.y) * (b.max.z - b.min.z);
      if (v < bestVol) { bestVol = v; best = p; }
    }
    return best;
  }

  nearestMarker(point) {
    let best = null, bestD = Infinity;
    for (const [name, m] of Object.entries(this.ctx.level.markers || {})) {
      const d = Math.hypot(m.x - point.x, (m.y ?? 0) - point.y, m.z - point.z);
      if (d < bestD) { bestD = d; best = { name, d, m }; }
    }
    return best;
  }

  update() {
    if (!this.visible) return;
    const { camera, scene, player, cats } = this.ctx;

    this.ray.setFromCamera(this.centre, camera);
    const hits = this.ray.intersectObjects(scene.children, true)
      .filter((h) => h.object.isMesh && !this.isCat(h.object));

    const p = player.pos;
    const lines = [];
    lines.push(row("cat", `${f(p.x)} ${f(p.y)} ${f(p.z)}`,
      `${player.grounded ? "grounded" : "airborne"}  ${player.state}`));
    lines.push(row("facing", `${(player.facing * 180 / Math.PI).toFixed(0)}°`,
      `speed ${player.speed.toFixed(2)} m/s`));
    // lastPower is the readout that matters for tuning: charge is back to 0
    // by the first airborne frame, so only the stored multiplier can say
    // what a given hold actually launched at (1.00 tap .. 1.50 full).
    lines.push(row("charge", player.charge.toFixed(2),
      `last launch ${player.lastPower.toFixed(2)}x`));

    if (hits.length) {
      const hit = hits[0];
      const piece = this.pieceAt(hit.point);
      const mark = this.nearestMarker(hit.point);
      lines.push("<hr>");
      lines.push(row("point", `${f(hit.point.x)} ${f(hit.point.y)} ${f(hit.point.z)}`,
        `${hit.distance.toFixed(2)} m away`));
      lines.push(row("module", piece ? piece.owner : "—",
        piece ? piece.material : hit.object.material?.name || ""));
      if (piece) {
        const b = piece.box;
        const c = b.getCenter(new THREE.Vector3());
        const s = b.getSize(new THREE.Vector3());
        lines.push(row("piece", `centre ${f(c.x)} ${f(c.y)} ${f(c.z)}`,
          `size ${s.x.toFixed(2)}×${s.y.toFixed(2)}×${s.z.toFixed(2)}`));
      }
      if (mark && mark.d < 8) {
        lines.push(row("marker", mark.name, `${mark.d.toFixed(2)} m`));
      }
      this.last = { hit, piece, mark };
    } else {
      this.last = null;
      lines.push("<hr>");
      lines.push(row("point", "—", "nothing under the crosshair"));
    }

    lines.push("<hr>");
    lines.push(row("cat model", cats?.[this.activeCat()]?.meta?.id || "?",
      `${this.ctx.level.triangles} tris  ${this.ctx.level.colliders.length} colliders`));
    lines.push(`<div class="hint">X copies this &middot; F3 or \` hides</div>`);
    this.body.innerHTML = lines.join("");
  }

  activeCat() {
    return (this.ctx.cats || []).findIndex((c) => c.root.visible);
  }

  isCat(obj) {
    let o = obj;
    while (o) {
      if ((this.ctx.cats || []).some((c) => c.root === o)) return true;
      o = o.parent;
    }
    return false;
  }

  /** One pasteable line describing whatever is under the crosshair. */
  describe() {
    const p = this.ctx.player.pos;
    const at = `cat at (${f(p.x)}, ${f(p.y)}, ${f(p.z)})`;
    if (!this.last) return `${at} — nothing under the crosshair`;
    const { hit, piece, mark } = this.last;
    const where = `point (${f(hit.point.x)}, ${f(hit.point.y)}, ${f(hit.point.z)})`;
    if (!piece) return `${at} — ${where}`;
    const b = piece.box;
    const c = b.getCenter(new THREE.Vector3());
    const s = b.getSize(new THREE.Vector3());
    let out = `${piece.owner} / ${piece.material} — ${where}, ` +
      `piece centre (${f(c.x)}, ${f(c.y)}, ${f(c.z)}) ` +
      `size ${s.x.toFixed(2)}x${s.y.toFixed(2)}x${s.z.toFixed(2)}`;
    if (mark && mark.d < 8) out += `, near marker "${mark.name}" (${mark.d.toFixed(2)} m)`;
    return `${out}; ${at}`;
  }

  copy() {
    const text = this.describe();
    const done = () => {
      this.toast.textContent = "copied";
      this.toast.classList.remove("hidden");
      clearTimeout(this._t);
      this._t = setTimeout(() => this.toast.classList.add("hidden"), 1200);
    };
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text).then(done, () => fallback(text, done));
    } else {
      fallback(text, done);
    }
  }
}

// Pointer lock and iframes both break clipboard writes in some browsers; drop
// the text into a selected textarea so it can still be copied by hand.
function fallback(text, done) {
  const ta = document.createElement("textarea");
  ta.value = text;
  ta.style.cssText = "position:fixed;bottom:8px;left:8px;width:60vw;z-index:99";
  document.body.appendChild(ta);
  ta.select();
  try { document.execCommand("copy"); done(); } catch { /* leave it selected */ }
  setTimeout(() => ta.remove(), 4000);
}

const f = (n) => (n >= 0 ? " " : "") + n.toFixed(2);
const row = (k, v, extra = "") =>
  `<div class="r"><b>${k}</b><span>${v}</span><i>${extra}</i></div>`;
